import React, { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useTheme } from '../ThemeContext';
import { getUnreadCount } from '../api';
import {
  LayoutDashboard, Globe, Puzzle, Palette, Upload,
  RefreshCw, Clock, Settings, LogOut, Sun, Moon, Bell, Trash2, Boxes, Mail
} from 'lucide-react';
import IdleLogout from './IdleLogout';
import './Layout.css';

const nav = [
  { section: 'Overview' },
  { to: '/',        icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/sites',   icon: Globe,           label: 'My Sites' },
  { section: 'Manage' },
  { to: '/plugins', icon: Puzzle,          label: 'Plugins' },
  { to: '/themes',  icon: Palette,         label: 'Themes' },
  { to: '/upload',  icon: Upload,          label: 'Upload & Deploy' },
  { to: '/core',    icon: RefreshCw,       label: 'Core Updates' },
  { to: '/forms',   icon: Trash2,          label: 'Form Cleanup' },
  { to: '/email',   icon: Mail,            label: 'Email Cleanup' },
  { to: '/agent',   icon: Boxes,           label: 'Agent' },
  { section: 'Account' },
  { to: '/notifications', icon: Bell,      label: 'Notifications', badge: true },
  { to: '/activity',icon: Clock,           label: 'Activity Log' },
  { to: '/settings',icon: Settings,        label: 'Settings' },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => getUnreadCount().then(r => { if (alive) setUnread(r.count || 0); }).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    const onFocus = () => load();
    window.addEventListener('wpm:notifications-read', onFocus);
    return () => { alive = false; clearInterval(t); window.removeEventListener('wpm:notifications-read', onFocus); };
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M3.6 9h16.8M3.6 15h16.8M12 3a14 14 0 0 0 0 18M12 3a14 14 0 0 1 0 18" />
            </svg>
          </div>
          <div>
            <div className="brand-name">WP Manager</div>
            <div className="brand-plan">Admin</div>
          </div>
        </div>

        <nav className="nav">
          {nav.map((item, i) =>
            item.section ? (
              <div key={'s' + i} className="nav-section">{item.section}</div>
            ) : (
              <NavLink key={item.to} to={item.to} end={item.to === '/'}
                className={({ isActive }) => 'nav-item' + (isActive ? ' active' : '')}>
                <item.icon size={17} />
                <span>{item.label}</span>
                {item.badge && unread > 0 && <span className="nav-badge">{unread > 99 ? '99+' : unread}</span>}
              </NavLink>
            )
          )}
        </nav>

        <div className="sidebar-foot">
          <div className="theme-toggle" style={{ display: 'flex', marginBottom: 10 }}>
            <button className={theme === 'light' ? 'active' : ''} onClick={() => setTheme('light')} title="Light mode" style={{ flex: 1 }}>
              <Sun size={15} />
            </button>
            <button className={theme === 'dark' ? 'active' : ''} onClick={() => setTheme('dark')} title="Dark mode" style={{ flex: 1 }}>
              <Moon size={15} />
            </button>
          </div>
          <div className="user-chip">
            <div className="avatar">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="user-info">
              <div className="user-name">{user?.name}</div>
              <div className="user-email">{user?.email}</div>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}><LogOut size={14} /> Logout</button>
        </div>
      </aside>

      <main className="main">
        <Outlet />
      </main>

      {/* Signs the browser out after a stretch of no activity (off, or 15/30/60
          min, chosen in Settings). Only mounted here, behind the login gate. */}
      <IdleLogout />
    </div>
  );
}
